import {type ImgHTMLAttributes, type ReactNode, forwardRef, useRef} from 'react';
import {X} from 'lucide-react';
import {Button, type ButtonProps} from '../button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogVisuallyHidden,
} from '../dialog';
import {cn} from '../lib/cn';
import {useScrollFrameIntoView} from '../hooks';

type Text = {
  title?: string | ReactNode;
  text?: string | ReactNode;
};

type Icon = {
  icon?: ReactNode;
  iconSrc?: string;
  iconAlt?: string;
  iconProps?: ImgHTMLAttributes<HTMLImageElement>;
  iconClassName?: string;
};

type Buttons = {
  buttons?: ReactNode;
};

type Close = {
  withCloseButton?: boolean;
  closeOnDocumentClick?: boolean;
  closeOnEscape?: boolean;
};

type ModalSize = 'auto' | 'compact';

export type ModalProps = {
  open?: boolean;
  modal?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  children?: ReactNode;
  className?: string;
  lockScroll?: boolean;
  scrollableOverlay?: boolean;
  container?: HTMLElement;
  overlayClassName?: string;
  size?: ModalSize;
} & Close &
  Text &
  Icon &
  Buttons;

const preventDefault = (event: Event) => {
  event.preventDefault();
};

function Modal({
  open,
  onOpenChange,
  onClose,
  children,
  withCloseButton = true,
  closeOnDocumentClick = false,
  closeOnEscape = false,
  scrollableOverlay,
  className,
  title,
  text,
  icon,
  iconSrc,
  iconAlt,
  iconProps = {width: 84},
  iconClassName,
  buttons,
  lockScroll = true,
  container,
  modal,
  overlayClassName,
  size = 'auto',
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useScrollFrameIntoView(open, {elementRef: contentRef});

  const handleClose = () => {
    onOpenChange?.(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent
        ref={contentRef}
        showCloseButton={false}
        onPointerDownOutside={closeOnDocumentClick ? handleClose : preventDefault}
        onEscapeKeyDown={closeOnEscape ? handleClose : preventDefault}
        container={container}
        overlayClassName={overlayClassName}
        className={cn(
          'modal__content',
          'flex h-auto min-w-[340px] w-auto flex-col items-center gap-y-6 bg-[var(--chekin-color-white)] text-center text-[var(--chekin-color-brand-navy)]',
          scrollableOverlay && 'min-h-0',
          size === 'compact' && 'w-[360px] min-w-0',
          className,
        )}
        lockScroll={lockScroll}
        {...(!text && {'aria-describedby': undefined})}
      >
        {withCloseButton && (
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'modal__close',
              'absolute right-4 top-4 z-10 rounded-full p-1 text-[var(--chekin-color-brand-blue)] hover:bg-[var(--chekin-neutral-50)]',
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {(icon || iconSrc || iconProps?.src) && (
          <div className={cn('modal__icon mx-auto mt-4 select-none', iconClassName)}>
            {icon ?? <img src={iconSrc} alt={iconAlt ?? ''} {...iconProps} />}
          </div>
        )}
        {title ? (
          <DialogTitle className={cn('modal__title', 'px-6 font-bold')}>
            {title}
          </DialogTitle>
        ) : (
          <DialogVisuallyHidden>
            <DialogTitle>Dialog</DialogTitle>
          </DialogVisuallyHidden>
        )}
        {text && (
          <DialogDescription className={cn('modal__text', 'text-base')}>
            {text}
          </DialogDescription>
        )}
        {children}
        {buttons && (
          <div
            className={cn(
              'modal__buttons-wrapper',
              'mb-2 mt-6 flex flex-col items-center justify-center gap-y-4',
            )}
          >
            {buttons}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ModalButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({children, size, className, ...props}, ref) => (
    <Button
      ref={ref}
      className={cn('modal__button', 'min-w-[210px]', className)}
      size={size && 'lg'}
      {...props}
    >
      {children}
    </Button>
  ),
);

ModalButton.displayName = 'ModalButton';
Modal.displayName = 'Modal';

export {Modal, ModalButton, type ModalSize};
